import Decimal from "decimal.js";
import { GlobalStore } from "store/global/GlobalStore";
import { TokenList } from "store/global/getAllTokenLists";
import { Balances } from "store/global/getBalances";
import { Amount } from "types/amount";
import { Chain } from "wagmi";
import { Token } from "../store/global/TokenEnum";
import { getWrappedSymbolForChain } from "./chainUtils";
import { getPriceForTokenByTokenList } from "./tokenListUtils";
import { isNativeToken } from "./wrapUtils";
import { CounterParty } from "./tradeUtils";
import percentRound from "percent-round";

export const sortBalancesInDescOrder = (list: Balances, tokenList?: TokenList): Balances => {
  return [...list].sort((a, b) => {
    const aPrice = getPriceForTokenByTokenList(a.symbol, tokenList);
    const bPrice = getPriceForTokenByTokenList(b.symbol, tokenList);
    const aAmount = aPrice.mul(a.balance);
    const bAmount = bPrice.mul(b.balance);
    if (bAmount.value.gt(0) || aAmount.value.gt(0)) {
      const result = bAmount.value.sub(aAmount.value).gt(new Decimal(0)) ? 1 : -1;
      return result;
    } else {
      return a.symbol.localeCompare(b.symbol);
    }
  });
};

// NOTE: "Natives" stand for either wrapped or native tokens
// Reasons below describe the current state that prevents addition or changing of tokens
export enum TokenRowDisabledReason {
  OppositePartyHasNativesAndIsNon121Trade = "OppositePartyHasNativesAndIsNon121Trade",
  SelectedTokensAlreadyIncludesNatives = "SelectedTokensAlreadyIncludesNatives",
  IsWrapTrade = "IsWrapTrade",
  CantHaveNativeAndWrappedOnOppositeSidesInManyTrade = "CantHaveNativeAndWrappedOnOppositeSidesInManyTrade",
  ChangeToWrappedNotAllowedDueToNativeInCurrentParty = "ChangeToWrappedNotAllowedDueToNativeInCurrentParty",
  ChangeToNativeNotAllowedDueToWrappedInCurrentParty = "ChangeToNativeNotAllowedDueToWrappedInCurrentParty",
}

export const getTokenRowDisabledReason = (
  symbol: Token,
  network: Chain,
  counterParty: CounterParty,
  takerTokensArray: Array<Token>,
  makerTokensArray: Array<Token>,
  tokenList: TokenList | undefined,
  isChangingToken: Token | undefined
): TokenRowDisabledReason | null => {
  const wrappedSymbolForChain = getWrappedSymbolForChain(network);
  const nativeSymbolForChain = network.nativeCurrency.symbol as Token;
  const currentSymbolIsNative = symbol === nativeSymbolForChain;
  const currentSymbolIsWrapped = symbol === wrappedSymbolForChain;
  const currentPartyTokens = counterParty === "maker" ? makerTokensArray : takerTokensArray;
  const oppositePartyTokens = counterParty === "maker" ? takerTokensArray : makerTokensArray;
  const nativeInOppositeParty = oppositePartyTokens.includes(nativeSymbolForChain);
  const wrappedInOppositeParty = oppositePartyTokens.includes(wrappedSymbolForChain);
  const wrappedInCurrentParty = currentPartyTokens.includes(wrappedSymbolForChain);
  const nativeInCurrentParty = currentPartyTokens.includes(nativeSymbolForChain);
  const willBe121 = oppositePartyTokens.length === 1 && currentPartyTokens.length === 0; // is121 on next token selected for current party

  if (isChangingToken) {
    if (currentPartyTokens.length > 1 && nativeInOppositeParty && currentSymbolIsWrapped) {
      return TokenRowDisabledReason.CantHaveNativeAndWrappedOnOppositeSidesInManyTrade;
    }

    if (currentPartyTokens.length > 1 && wrappedInOppositeParty && currentSymbolIsNative) {
      return TokenRowDisabledReason.CantHaveNativeAndWrappedOnOppositeSidesInManyTrade;
    }

    if (
      isChangingToken !== nativeSymbolForChain &&
      currentPartyTokens.length > 1 &&
      nativeInCurrentParty &&
      currentSymbolIsWrapped
    ) {
      return TokenRowDisabledReason.ChangeToWrappedNotAllowedDueToNativeInCurrentParty;
    }
    if (
      isChangingToken !== wrappedSymbolForChain &&
      currentPartyTokens.length > 1 &&
      wrappedInCurrentParty &&
      currentSymbolIsNative
    ) {
      return TokenRowDisabledReason.ChangeToNativeNotAllowedDueToWrappedInCurrentParty;
    }
  } else {
    if (
      (currentSymbolIsNative && wrappedInOppositeParty && !willBe121) ||
      (currentSymbolIsWrapped && nativeInOppositeParty && !willBe121)
    ) {
      return TokenRowDisabledReason.OppositePartyHasNativesAndIsNon121Trade;
    }
  }

  const isNativeAndWrappedSelectedInSameParty =
    currentPartyTokens.includes(wrappedSymbolForChain) && currentSymbolIsNative;
  const isWrappedAndNativeSelectedInSameParty =
    currentPartyTokens.includes(nativeSymbolForChain) && currentSymbolIsWrapped;
  if (
    (isNativeAndWrappedSelectedInSameParty && !isChangingToken) ||
    (isWrappedAndNativeSelectedInSameParty && !isChangingToken)
  ) {
    return TokenRowDisabledReason.SelectedTokensAlreadyIncludesNatives;
  }

  const isWrappingFlow =
    (currentPartyTokens.length === 1 &&
      currentPartyTokens[0] === nativeSymbolForChain &&
      oppositePartyTokens.length === 1 &&
      oppositePartyTokens[0] === wrappedSymbolForChain) ||
    (currentPartyTokens.length === 1 &&
      currentPartyTokens[0] === wrappedSymbolForChain &&
      oppositePartyTokens.length === 1 &&
      oppositePartyTokens[0] === nativeSymbolForChain) ||
    (symbol !== nativeSymbolForChain &&
      currentPartyTokens.length === 1 &&
      currentPartyTokens[0] === nativeSymbolForChain &&
      oppositePartyTokens.length === 1 &&
      oppositePartyTokens[0] === wrappedSymbolForChain) ||
    (symbol !== wrappedSymbolForChain &&
      currentPartyTokens.length === 1 &&
      currentPartyTokens[0] === wrappedSymbolForChain &&
      oppositePartyTokens.length === 1 &&
      oppositePartyTokens[0] === nativeSymbolForChain);

  if (isWrappingFlow && !isChangingToken) {
    return TokenRowDisabledReason.IsWrapTrade;
  }

  return null;
};

export const getIsWrapOrUnwrap = (
  takerTokens: Array<Token>,
  makerTokens: Array<Token>,
  chain: Chain
) => {
  if (takerTokens.length !== 1 || makerTokens.length !== 1) {
    return false;
  }

  if (
    (isNativeToken(takerTokens[0], chain) && makerTokens[0] === getWrappedSymbolForChain(chain)) ||
    (isNativeToken(makerTokens[0], chain) && takerTokens[0] === getWrappedSymbolForChain(chain))
  ) {
    return true;
  }
  return false;
};

export const addTokenToStore = (counterParty: CounterParty, token: Token) => {
  GlobalStore.update(s => {
    s.isRequestingQuote = false;
    s.quote = undefined;
  });

  if (counterParty === "taker") {
    GlobalStore.update(s => {
      s.takerTokens = [...s.takerTokens, { token: token, amount: Amount.ZERO }];
    });
  } else if (counterParty === "maker") {
    GlobalStore.update(s => {
      s.makerTokens = [...s.makerTokens.map(entry => entry.token), token].map((token, _, arr) => {
        return {
          token,
          amount: Amount.ZERO,
          pct: 100 / arr.length,
          isLocked: false,
        };
      });
    });
  }
};

export const removeTokenFromStore = (counterParty: CounterParty, token: Token) => {
  if (counterParty === "taker") {
    GlobalStore.update(s => {
      s.takerTokens = s.takerTokens.filter(entry => entry.token !== token);
    });
  } else if (counterParty === "maker") {
    GlobalStore.update(s => {
      s.makerTokens = s.makerTokens
        .filter(entry => entry.token !== token)
        .map((entry, _, arr) => {
          return {
            token: entry.token,
            amount: Amount.ZERO,
            pct: 100 / arr.length,
            isLocked: false,
          };
        });
    });
  }
};

export const changeTokenOnStore = (counterParty: CounterParty, token: Token, newToken: Token) => {
  if (counterParty === "taker") {
    GlobalStore.update(s => {
      const newTakerTokens = [
        ...s.takerTokens.map(entry =>
          entry.token === token ? { token: newToken, amount: Amount.ZERO } : entry
        ),
      ];
      s.takerTokens = newTakerTokens;
    });
  } else if (counterParty === "maker") {
    GlobalStore.update(s => {
      const newMakerTokens = [
        ...s.makerTokens.map((entry, index) => {
          if (entry.token === token) {
            const makerArray = Object.keys(s.makerTokens);
            const newPercentages: Array<number> = new Array(makerArray.length).fill(
              100 / makerArray.length
            );
            const roundedPercentages = percentRound(newPercentages);
            return {
              token: newToken,
              amount: Amount.ZERO,
              pct: roundedPercentages[index],
              isLocked: false,
            };
          }
          return entry;
        }),
      ];
      s.makerTokens = newMakerTokens;
    });
  }
};
